import { create } from "zustand";
import type {
  ArrowCorner,
  ArrowEdge,
  ConnectorSide,
  StampKind,
  StrokePattern,
  TextAlign,
} from "@refinement-canvas/domain";
import type { ViewState } from "../../../lib/canvas-coordinates";
import type { MarqueeRect } from "../../../lib/marquee";
import { loadGridSnapPreference, saveGridSnapPreference } from "../../../lib/grid-snap-preference";
import {
  loadSelectPanInvertPreference,
  saveSelectPanInvertPreference,
} from "../../../lib/select-pan-invert-preference";
import {
  loadPenColor,
  loadPenStrokePattern,
  loadPenThickness,
  savePenStrokePattern,
  savePenThickness,
  type PenColor,
  type PenThickness,
} from "../../../lib/pen-preference";
import { loadStickyColor, type StickyColor } from "../../../lib/sticky-preference";
import {
  loadShapeFill,
  loadShapeStroke,
  loadShapeStrokePattern,
  loadShapeStrokeWidth,
  saveShapeStrokePattern,
  saveShapeStrokeWidth,
  type ShapeFill,
  type ShapeStrokeWidth,
} from "../../../lib/shape-preference";
import { loadSectionFill, saveSectionFill } from "../../../lib/section-preference";
import { loadTextFontSize, saveTextFontSize } from "../../../lib/text-size-preference";
import { loadTextAlign, saveTextAlign } from "../../../lib/text-align-preference";
import {
  loadTextBold,
  loadTextColor,
  loadTextFill,
  normalizeTextColorPreference,
  normalizeTextFillPreference,
  saveTextBold,
  saveTextColor,
  saveTextFill,
} from "../../../lib/text-style-preference";

// キャンバス UI のローカル状態を管理する Zustand store。
// pan/zoom、選択中ツール、選択中オブジェクトなど高頻度更新の値を保持する。

export type CanvasTool =
  | "idle"
  | "panning"
  | "place-sticky"
  | "place-section"
  | "place-shape"
  | "place-text"
  | "draw-pen"
  | "place-table"
  | "place-stamp"
  | "place-comment";

export type ShapeKind = "circle" | "rectangle" | "arrow";

/**
 * 「既存オブジェクトの上でも新規配置を通す」配置ツールか（付箋/図形/テキスト/スタンプ/コメント）。
 * 既存オブジェクト本体・コネクタ・選択ハンドル等が pointerdown を止めずに stage 配置へ譲るかの判定を
 * 1 箇所へ集約する（経路ごとに許可リストが重複して追従漏れが起きるのを防ぐ）。新しい配置ツールを
 * 追加するときは、この述語だけを更新すれば全経路へ反映される。
 */
export function isObjectPlacementTool(tool: CanvasTool): boolean {
  return (
    tool === "place-sticky" ||
    tool === "place-section" ||
    tool === "place-shape" ||
    tool === "place-text" ||
    tool === "place-table" ||
    tool === "place-stamp" ||
    tool === "place-comment"
  );
}

/**
 * 既存オブジェクトの上でもカーソルを crosshair（新規作成・描画の意思表示）にすべきツールか。
 * 配置ツール（付箋/図形/テキスト/スタンプ/コメント）に加えて、ペン描画（draw-pen）も含める。
 * ペンは既存オブジェクトの上にも描けるが、object 側の cursor が `isObjectPlacementTool` だけを
 * 見ていると draw-pen が move のままになり「移動できる」と誤認させてしまう（特に図形は内側 SVG が
 * pointerEvents:all のため SVG の cursor を継承して move になる）。カーソル表示専用の判定として
 * これを使い、pointerdown の配置ルーティングに使う `isObjectPlacementTool` とは分離する。
 */
export function usesCrosshairCursor(tool: CanvasTool): boolean {
  return isObjectPlacementTool(tool) || tool === "draw-pen";
}

/**
 * オブジェクト本体のカーソルを決める。
 *
 * 参照専用（埋め込み表示）では移動・リサイズ・回転・テキスト編集をすべて塞いでいるため、
 * 動かせることを示す move も編集できることを示す text も出さず、default へ統一する
 * （move は Windows の Chrome で四方向矢印として描画され、「つかんで動かせる」という
 * 誤った期待を与えるため）。readOnly を最優先に置くのは、参照専用では編集にもツール操作にも
 * 入れず、後続の判定が成立し得ないからである。
 *
 * リンクカード（参照専用でも pointer）やコネクタのように独自のカーソルを持つオブジェクトは、
 * この関数を呼ばずに自分で決める。
 */
export function resolveObjectCursor(options: {
  readonly readOnly: boolean;
  readonly activeTool: CanvasTool;
  readonly isEditing?: boolean | undefined;
}): "default" | "text" | "crosshair" | "move" {
  if (options.readOnly) return "default";
  if (options.isEditing === true) return "text";
  if (usesCrosshairCursor(options.activeTool)) return "crosshair";
  return "move";
}

export interface PointerPosition {
  readonly x: number;
  readonly y: number;
}

/** 右クリックで開くコンテキストメニューの状態（クライアント座標）。 */
export interface ContextMenuState {
  readonly objectId: string;
  readonly clientX: number;
  readonly clientY: number;
}

/**
 * 何もない空白部を右クリックしたときに開くコンテキストメニューの状態。
 * clientX/Y はメニューの表示位置（画面座標）、stageX/Y は貼り付け先のステージ座標
 * （開いた瞬間の右クリック位置を保持し、以後のマウス移動で貼り付け先がずれないようにする）。
 */
export interface EmptyContextMenuState {
  readonly clientX: number;
  readonly clientY: number;
  readonly stageX: number;
  readonly stageY: number;
  /**
   * メニューを開いた contextmenu の時点で、まだマウスのボタンが押されたままだったか
   * （contextmenu の buttons が 0 以外）。
   *
   * contextmenu の発火時点はブラウザで異なり、Windows は pointerup の後（buttons === 0）だが、
   * Linux の Chromium は pointerdown の時点（buttons !== 0）で発火する。後者ではメニューが
   * ドラッグ中のポインタの真下に現れるため、そのまま受け付けると右ボタンドラッグの範囲指定
   * （use-marquee-selection）の続きをメニューが奪ってしまう。ボタンが離されるまでメニューを
   * 素通しにする判定に使う。
   */
  readonly pointerHeld: boolean;
}

/**
 * コネクタ（接続矢印）作成中の一時状態。
 * 接続元（fromObjectId / fromSide）からポインタ位置（ステージ座標）へゴースト線を引き、
 * 接続先候補が見つかれば target に保持して 4 辺ドットを表示・スナップする。
 */
export interface ConnectionDraftState {
  readonly fromObjectId: string;
  readonly fromSide: ConnectorSide;
  readonly pointer: PointerPosition;
  readonly target: { readonly objectId: string; readonly side: ConnectorSide } | null;
  /**
   * 現在のポインタ座標がキャンバス面（canvas-container）の内側かどうか。
   * ツールバー・ミニマップ・メニュー・コメントピンの上で離したときに、キャンバスへ
   * オブジェクトを自動作成してしまわないための判定結果を保持する
   * （とくにミニマップ上でのリリースは、画面外の遠方座標に作られる最悪ケースになる）。
   *
   * ポインタ座標が更新されるたび（pointermove / 確定の pointerup・pointerdown）に
   * 再計算する。ゴースト枠の表示と確定時の判定が同じ値を見ることで、
   * 「表示されていた内容」と「実際に作られる結果」の乖離を構造的に防ぐ。
   * 接続開始時点ではまだポインタ座標での判定を行っていないため false から始める。
   */
  readonly onCanvasSurface: boolean;
}

/**
 * 図形をドラッグで配置している最中のプレビュー枠（ステージ座標）。
 * ドラッグしていないときは null。確定（pointerup）時にこのサイズで図形を作成する。
 * 矢印のときはドラッグ方向に応じた起点角と、軸そろえ時の起点辺を保持する。
 */
export interface ShapePlacementDraft {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
  readonly arrowFromCorner?: ArrowCorner | undefined;
  readonly arrowFromEdge?: ArrowEdge | undefined;
}

/**
 * コメント配置（place-comment）でキャンバスをクリックした位置（ステージ座標）。
 * この値がセットされている間、その位置にコメント入力ウィンドウを表示する。
 * 投稿確定・キャンセル・モードリセットで null に戻す。
 */
export interface CommentDraftState {
  readonly x: number;
  readonly y: number;
}

/**
 * テキスト図形を編集中の「非空な文字範囲選択」。範囲専用の書式パネルを表示する条件そのもので、
 * 永続化しない一時状態として保持する。
 *
 * - `start` / `end` は保存本文（生 Markdown）先頭からの UTF-16 offset（半開区間）。
 * - `text` はその offset が指す本文そのもの。dispatch 時に保存本文と照合し、選択後に本文が
 *   変わっていた場合は「別の文字へ書式が付く」前に操作を止めるために持つ。
 * - `anchorRect` は選択矩形（ビューポート座標）。複数行選択では全矩形を包含する矩形。
 * - `panelInteracting` は「パネル操作中で、DOM 選択が一時的に外れてもこの範囲を保持する」印。
 *   色入力など入力を要する UI にフォーカスが移っても、操作完了まで対象範囲を失わないようにする。
 */
export interface TextRangeSelectionState {
  readonly objectId: string;
  readonly start: number;
  readonly end: number;
  readonly text: string;
  readonly anchorRect: {
    readonly x: number;
    readonly y: number;
    readonly w: number;
    readonly h: number;
  };
  readonly panelInteracting: boolean;
}

/**
 * 表のどのセルを編集中かを表す。null は非編集。
 *
 * オブジェクトをまたいで残らないよう、キャンバス全体で 1 件だけ保持する
 * （テキスト・付箋の編集と同時に成立させない）。
 */
export interface TableCellEditing {
  readonly objectId: string;
  readonly row: number;
  readonly column: number;
}

interface CanvasUiState {
  view: ViewState;
  tool: CanvasTool;
  shapeKind: ShapeKind;
  /** スタンプ配置時に使う絵文字種別。place-stamp ツール時のサブ選択結果を保持する。 */
  stampKind: StampKind;
  penColor: PenColor;
  /** ペン描画で使用する線種。 */
  penStrokePattern: StrokePattern;
  /** ペンの太さ（細 / 中 / 太）。描画時の線幅（PEN_THICKNESS_PIXELS）へ変換して使う。 */
  penThickness: PenThickness;
  stickyColor: StickyColor;
  /** 図形配置で使用する背景色（fill）。"none"（背景なし）または hex 文字列を保持する。 */
  shapeFill: ShapeFill;
  /** 図形配置で使用する枠線色。"none"（線なし）または hex 文字列を保持する。 */
  shapeStroke: string;
  /** 図形配置で使用する枠線（矢印では線）の種類。 */
  shapeStrokePattern: StrokePattern;
  /** 図形配置で使用する枠線（矢印では線）の太さプリセット。 */
  shapeStrokeWidth: ShapeStrokeWidth;
  /** セクション配置で使用する背景色（fill）。常に hex 文字列（「背景なし」は提供しない）。 */
  sectionFill: string;
  /** テキスト配置で使用する初期 font-size（px 値）。プリセット（小/中/大）・px 指定の両方を保持する。 */
  textFontSize: number;
  /** テキスト・付箋配置で使用する初期の文字寄せ（左/中央/右）。既定は左寄せ。 */
  textAlign: TextAlign;
  /** テキスト配置で使用する初期の文字色。常に 6 桁 hex。 */
  textColor: string;
  /** テキスト配置で使用する初期の背景色。6 桁 hex、または背景なしを表す "transparent"。 */
  textFill: string;
  /** テキスト配置で使用する初期の太字。既定は false（通常ウェイト）。 */
  textBold: boolean;
  selectedObjectIds: readonly string[];
  /**
   * 配置直後に自動で編集モードへ入れたいオブジェクトの id。
   * テキスト配置（place-text）で空のテキスト図形を作った直後にセットし、対象の TextObject が
   * マウント時に編集モードへ入って即入力できるようにする。処理後は対象コンポーネントが null に戻す。
   */
  autoEditObjectId: string | null;
  /**
   * 範囲指定（マーキー）選択のドラッグ中に表示する選択枠（ステージ座標）。
   * ドラッグしていないときは null。確定時に枠へ重なるオブジェクトを選択する。
   */
  marqueeRect: MarqueeRect | null;
  lastPointerStagePosition: PointerPosition | null;
  /** グリッド吸着モード（ON のとき、ペン以外のオブジェクトは移動・リサイズ・配置時に CANVAS_SNAP_PX へ丸める）。 */
  gridSnapEnabled: boolean;
  /**
   * 選択操作の左右反転モード（既定 OFF）。
   * OFF: 空白部の左ドラッグ=キャンバス移動 / 右ドラッグ=範囲選択（従来）。
   * ON : 空白部の右ドラッグ=キャンバス移動 / 左ドラッグ=範囲選択（反転）。
   * どちらのモードでも図形選択は左シングルクリック、図形コンテキストメニューは右シングルクリックのまま。
   */
  invertSelectPan: boolean;
  contextMenu: ContextMenuState | null;
  /** 空白部の右クリックで開く「貼り付け」コンテキストメニューの状態。開いていないときは null。 */
  emptyContextMenu: EmptyContextMenuState | null;
  /** コネクタ作成中の一時状態。作成していないときは null。 */
  connectionDraft: ConnectionDraftState | null;
  /** 図形をドラッグ配置中のプレビュー枠。ドラッグしていないときは null。 */
  shapePlacementDraft: ShapePlacementDraft | null;
  /** コメント入力ウィンドウの表示位置（新規スレッド配置中）。未配置時は null。 */
  commentDraft: CommentDraftState | null;
  /**
   * テキスト図形を編集中の非空な文字範囲選択。範囲専用書式パネルの表示条件そのもの。
   * 対象外状態（選択の折り畳み・編集終了・削除・ロック・ツール変更・外側押下・Escape）へ
   * 遷移したときは明示的に null へ戻す。
   */
  activeTextRangeSelection: TextRangeSelectionState | null;
  /**
   * 編集中の表のセル。編集していないときは null。
   * ツール切替・モードリセット・選択解除など、編集が続かない遷移では明示的に null へ戻す。
   */
  tableCellEditing: TableCellEditing | null;
  /**
   * 範囲専用パネルからの書式適用が成立するたびに 1 増える通知カウンタ。
   * 編集中の Ctrl+Z はエディタ自前のスナップショットスタックが正本のため、この通知を受けた
   * エディタが「変更前の run」を履歴へ積む。積まないと、適用直後の Ctrl+Z が範囲書式ではなく
   * 直前の入力へ消費され、1 書式操作を取り消せない。
   * 他参加者の書式変更と区別する必要があるため、run の変化だけを合図にはできない。
   */
  textRangeStyleAppliedSignal: number;
  /**
   * 参照専用モード（Connect Note からの埋め込み等）。true の間は編集系の入力・操作
   * （オブジェクト配置・移動・リサイズ・回転・削除・貼り付け・編集ショートカット・コメント投稿）を
   * 無効化し、パン / ズーム / リンク遷移 / コメント閲覧のみを許可する。
   */
  readOnly: boolean;

  /**
   * 親フレーム（Connect Note）から受け取った、キャンバス内で強調表示する検索語。
   * 空文字は「強調しない」を表す。参照専用モードでのみ値を持ち、保存データ・表示位置・拡大率・
   * 選択状態には一切影響しない（表示上の強調だけを担う一時状態）。
   */
  searchQuery: string;

  /**
   * view 同期（表示位置の保存 / URL 反映）の arm 要求シグナル（単調増加カウンタ）。
   * canvas-stage の arming は canvas-container 内のホイール / pointerdown / キー操作に限定されるため、
   * その外側（コメント UI 上の Ctrl+ホイールズーム等）で view を動かした操作を canvas-stage へ伝える。
   * requestViewSyncArm でインクリメントし、canvas-stage 側が変化を検知して arm する。
   */
  viewSyncArmSignal: number;

  setView: (view: ViewState) => void;
  /** view 同期の arm を要求する（viewSyncArmSignal をインクリメント）。 */
  requestViewSyncArm: () => void;
  setTool: (tool: CanvasTool) => void;
  setShapeKind: (shape: ShapeKind) => void;
  setStampKind: (kind: StampKind) => void;
  setPenColor: (color: PenColor) => void;
  setPenStrokePattern: (pattern: StrokePattern) => void;
  setPenThickness: (thickness: PenThickness) => void;
  setStickyColor: (color: StickyColor) => void;
  setShapeFill: (fill: ShapeFill) => void;
  setShapeStroke: (stroke: string) => void;
  setShapeStrokePattern: (pattern: StrokePattern) => void;
  setShapeStrokeWidth: (width: ShapeStrokeWidth) => void;
  setSectionFill: (fill: string) => void;
  /**
   * 配置前の文字サイズを更新する。契約は {@link CanvasUiState.setTextColor} と同じ
   * （楽観更新 → 保存失敗ならロールバックし false を返す）。
   */
  setTextFontSize: (px: number) => boolean;
  /** 配置前の文字寄せを更新する。契約は {@link CanvasUiState.setTextColor} と同じ。 */
  setTextAlign: (align: TextAlign) => boolean;
  /**
   * 配置前の文字色を更新する。画面を先に更新する楽観更新とし、localStorage への保存に
   * 失敗した場合は直前値へ戻す。受理できない値でも state を変えない。
   * 戻り値は「配置既定値として永続化できたか」で、通知の表示は呼び出し側の UI が担う
   * （store から通知 UI を直接呼ばない）。
   */
  setTextColor: (color: string) => boolean;
  /** 配置前の背景色を更新する。契約は {@link CanvasUiState.setTextColor} と同じ。 */
  setTextFill: (fill: string) => boolean;
  /** 配置前の太字を更新する。契約は {@link CanvasUiState.setTextColor} と同じ。 */
  setTextBold: (bold: boolean) => boolean;
  setSelectedObjectIds: (ids: readonly string[]) => void;
  setAutoEditObjectId: (id: string | null) => void;
  setMarqueeRect: (rect: MarqueeRect | null) => void;
  setLastPointerStagePosition: (point: PointerPosition | null) => void;
  setGridSnapEnabled: (enabled: boolean) => void;
  setInvertSelectPan: (enabled: boolean) => void;
  openContextMenu: (state: ContextMenuState) => void;
  closeContextMenu: () => void;
  openEmptyContextMenu: (state: EmptyContextMenuState) => void;
  closeEmptyContextMenu: () => void;
  setConnectionDraft: (draft: ConnectionDraftState | null) => void;
  setShapePlacementDraft: (draft: ShapePlacementDraft | null) => void;
  setCommentDraft: (draft: CommentDraftState | null) => void;
  /** テキスト範囲選択を設定・解除する（範囲専用書式パネルの表示切り替え）。 */
  setActiveTextRangeSelection: (selection: TextRangeSelectionState | null) => void;
  /** 表のセル編集を開始・移動・終了する（null で終了）。 */
  setTableCellEditing: (editing: TableCellEditing | null) => void;
  /**
   * パネル操作中フラグだけを切り替える。範囲が無いときは何もしない
   * （パネルを閉じた後のフォーカス移動で範囲が復活しないようにする）。
   */
  setTextRangePanelInteracting: (interacting: boolean) => void;
  /** 範囲専用パネルからの書式適用が成立したことを通知する（編集中 Undo 履歴への記録用）。 */
  notifyTextRangeStyleApplied: () => void;
  setReadOnly: (readOnly: boolean) => void;
  /**
   * 検索語を設定する。参照専用（readOnly）でないときは、編集ビューへ強調表示が漏れないよう
   * 常に空文字として扱う（受信ハンドラーの未設置・postToParent の親チェックと合わせた三重の担保）。
   */
  setSearchQuery: (query: string) => void;
  resetMode: () => void;
}

export const useCanvasStore = create<CanvasUiState>((set, get) => ({
  view: { panX: 0, panY: 0, zoom: 1 },
  tool: "idle",
  shapeKind: "rectangle",
  // スタンプは最初の選択肢を既定にしておく（明示的に切替するまで「いいね」スタンプが配置される）。
  stampKind: "thumbs-up",
  penColor: loadPenColor(),
  penStrokePattern: loadPenStrokePattern(),
  penThickness: loadPenThickness(),
  stickyColor: loadStickyColor(),
  shapeFill: loadShapeFill(),
  shapeStroke: loadShapeStroke(),
  shapeStrokePattern: loadShapeStrokePattern(),
  shapeStrokeWidth: loadShapeStrokeWidth(),
  sectionFill: loadSectionFill(),
  textFontSize: loadTextFontSize(),
  textAlign: loadTextAlign(),
  textColor: loadTextColor(),
  textFill: loadTextFill(),
  textBold: loadTextBold(),
  selectedObjectIds: [],
  autoEditObjectId: null,
  marqueeRect: null,
  lastPointerStagePosition: null,
  gridSnapEnabled: loadGridSnapPreference(),
  invertSelectPan: loadSelectPanInvertPreference(),
  contextMenu: null,
  emptyContextMenu: null,
  connectionDraft: null,
  shapePlacementDraft: null,
  commentDraft: null,
  activeTextRangeSelection: null,
  tableCellEditing: null,
  textRangeStyleAppliedSignal: 0,
  readOnly: false,
  searchQuery: "",
  viewSyncArmSignal: 0,

  setView: (view) => set({ view }),
  // 参照専用を解除する（編集ビューへ移る）ときは、検索語も必ず空へ戻す。
  setReadOnly: (readOnly) => set(readOnly ? { readOnly } : { readOnly, searchQuery: "" }),
  setSearchQuery: (query) => set((state) => ({ searchQuery: state.readOnly ? query : "" })),
  requestViewSyncArm: () => set((state) => ({ viewSyncArmSignal: state.viewSyncArmSignal + 1 })),
  // ツールを切り替えると編集は続かないため、テキスト範囲選択（範囲専用パネル）と
  // 表のセル編集も閉じる。
  setTool: (tool) =>
    set((state) =>
      state.activeTextRangeSelection === null && state.tableCellEditing === null
        ? { tool }
        : { tool, activeTextRangeSelection: null, tableCellEditing: null }
    ),
  setShapeKind: (shapeKind) => set({ shapeKind }),
  setStampKind: (stampKind) => set({ stampKind }),
  setPenColor: (penColor) => set({ penColor }),
  setPenStrokePattern: (penStrokePattern) => {
    savePenStrokePattern(penStrokePattern);
    set({ penStrokePattern });
  },
  setPenThickness: (penThickness) => {
    // 色サイズと同様、次回の描画時に再現できるようローカルストレージへ永続化する。
    savePenThickness(penThickness);
    set({ penThickness });
  },
  setStickyColor: (stickyColor) => set({ stickyColor }),
  setShapeFill: (shapeFill) => set({ shapeFill }),
  setShapeStroke: (shapeStroke) => set({ shapeStroke }),
  setShapeStrokePattern: (shapeStrokePattern) => {
    saveShapeStrokePattern(shapeStrokePattern);
    set({ shapeStrokePattern });
  },
  setShapeStrokeWidth: (shapeStrokeWidth) => {
    saveShapeStrokeWidth(shapeStrokeWidth);
    set({ shapeStrokeWidth });
  },
  setSectionFill: (sectionFill) => {
    // 図形・付箋と同様、次回の配置時に再現できるようローカルストレージへ永続化する。
    saveSectionFill(sectionFill);
    set({ sectionFill });
  },
  setTextFontSize: (px) => {
    // ローカルストレージにも永続化し、次回の挿入時に再現できるようにする。
    // 文字色・背景色・太字と同じ契約にそろえ、保存できなかったときは直前値へ戻す。
    const previous = get().textFontSize;
    set({ textFontSize: px });
    if (saveTextFontSize(px)) return true;
    set({ textFontSize: previous });
    return false;
  },
  setTextAlign: (align) => {
    // 文字サイズと同様、次回の挿入時に再現できるようローカルストレージへ永続化する。
    const previous = get().textAlign;
    set({ textAlign: align });
    if (saveTextAlign(align)) return true;
    set({ textAlign: previous });
    return false;
  },
  setTextColor: (color) => {
    const next = normalizeTextColorPreference(color);
    if (next === null) return false;
    const previous = get().textColor;
    // 画面を先に更新し、保存できなかったときだけ直前値へ戻す（楽観更新とロールバック）。
    set({ textColor: next });
    if (saveTextColor(next)) return true;
    set({ textColor: previous });
    return false;
  },
  setTextFill: (fill) => {
    const next = normalizeTextFillPreference(fill);
    if (next === null) return false;
    const previous = get().textFill;
    set({ textFill: next });
    if (saveTextFill(next)) return true;
    set({ textFill: previous });
    return false;
  },
  setTextBold: (bold) => {
    const previous = get().textBold;
    set({ textBold: bold });
    if (saveTextBold(bold)) return true;
    set({ textBold: previous });
    return false;
  },
  setSelectedObjectIds: (selectedObjectIds) => set({ selectedObjectIds }),
  setAutoEditObjectId: (autoEditObjectId) => set({ autoEditObjectId }),
  setMarqueeRect: (marqueeRect) => set({ marqueeRect }),
  setLastPointerStagePosition: (lastPointerStagePosition) => set({ lastPointerStagePosition }),
  setGridSnapEnabled: (enabled) => {
    saveGridSnapPreference(enabled);
    set({ gridSnapEnabled: enabled });
  },
  setInvertSelectPan: (enabled) => {
    // グリッド吸着と同様、次回の起動時に再現できるようローカルストレージへ永続化する。
    saveSelectPanInvertPreference(enabled);
    set({ invertSelectPan: enabled });
  },
  // オブジェクトメニューと空白部メニューは相互排他（片方を開くともう片方を閉じる）。
  openContextMenu: (contextMenu) => set({ contextMenu, emptyContextMenu: null }),
  closeContextMenu: () => set({ contextMenu: null }),
  openEmptyContextMenu: (emptyContextMenu) => set({ emptyContextMenu, contextMenu: null }),
  closeEmptyContextMenu: () => set({ emptyContextMenu: null }),
  setConnectionDraft: (connectionDraft) => set({ connectionDraft }),
  setShapePlacementDraft: (shapePlacementDraft) => set({ shapePlacementDraft }),
  setCommentDraft: (commentDraft) => set({ commentDraft }),
  setActiveTextRangeSelection: (activeTextRangeSelection) => set({ activeTextRangeSelection }),
  setTableCellEditing: (tableCellEditing) => set({ tableCellEditing }),
  setTextRangePanelInteracting: (interacting) =>
    set((state) =>
      state.activeTextRangeSelection === null
        ? state
        : {
            activeTextRangeSelection: {
              ...state.activeTextRangeSelection,
              panelInteracting: interacting,
            },
          }
    ),
  notifyTextRangeStyleApplied: () =>
    set((state) => ({ textRangeStyleAppliedSignal: state.textRangeStyleAppliedSignal + 1 })),
  resetMode: () =>
    set({
      tool: "idle",
      selectedObjectIds: [],
      contextMenu: null,
      emptyContextMenu: null,
      marqueeRect: null,
      connectionDraft: null,
      shapePlacementDraft: null,
      commentDraft: null,
      activeTextRangeSelection: null,
      tableCellEditing: null,
    }),
}));
